import React from 'react';
import {
  CreateFleetPayload,
  UpdateFleetPayload,
  activateFleetGroup,
  createFleet,
  deactivateFleetGroup,
  deleteFleetGroup,
  getFleetGroup,
  getOpsAuthority,
  updateFleetGroup,
} from '../../services/fleet';
import { SubFleetWorkspace } from './SubFleetWorkspace';

type FleetRecord = {
  id: string;
  name: string;
  description: string;
  type: 'fleet' | 'authority_group';
  status: string;
  parentId: string;
  childCount: number;
  depth: number;
};

export function SubFleets() {
  const [activeTab, setActiveTab] = React.useState<'groups' | 'sub-fleets'>('groups');
  const [fleets, setFleets] = React.useState<FleetRecord[]>([]);
  const [selectedFleet, setSelectedFleet] = React.useState<FleetRecord | null>(null);
  const [editingFleet, setEditingFleet] = React.useState<FleetRecord | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [createParentId, setCreateParentId] = React.useState<string | undefined>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [status, setStatus] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  const loadFleets = React.useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      setFleets(normalizeFleets(await getOpsAuthority()));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load fleet organization');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadFleets();
  }, [loadFleets]);

  const filteredFleets = fleets.filter((fleet) => {
    return !status || fleet.status === status;
  });

  return (
    <div className="fleet-organization-page">
      <nav className="fleet-organization-tabs" aria-label="Fleet organization sections">
        <button className={activeTab === 'groups' ? 'active' : ''} onClick={() => setActiveTab('groups')} type="button">Fleet groups</button>
        <button className={activeTab === 'sub-fleets' ? 'active' : ''} onClick={() => setActiveTab('sub-fleets')} type="button">Sub-fleets</button>
      </nav>
      {activeTab === 'sub-fleets' ? <SubFleetWorkspace fleets={fleets.filter((fleet) => fleet.type === 'fleet').map((fleet) => ({ id: fleet.id, name: fleet.name }))} /> : (
      <>
      <div className="fleet-organization-head">
        <div>
          <h2>Fleet organization</h2>
          <p>Organize fleets under operational authority groups.</p>
        </div>
        <div className="fleet-organization-actions">
          <button className="btn" disabled={isLoading} onClick={() => void loadFleets()} type="button">
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button className="btn btn-primary fleet-create-btn" onClick={() => { setCreateParentId(undefined); setIsCreating(true); }} type="button">
            <span aria-hidden="true">+</span> Create fleet
          </button>
        </div>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="fleet-organization-filters">
        <label>
          <span className="sr-only">Filter fleets by status</span>
          <select onChange={(event) => setStatus(event.target.value)} value={status}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>

      <section className="fleet-organization-list" aria-label="Fleet organization">
        {isLoading ? (
          <div className="empty"><h3>Loading fleet organization</h3><p>Fetching fleet groups and hierarchy.</p></div>
        ) : filteredFleets.length ? filteredFleets.map((fleet) => (
          <article className={`fleet-organization-row depth-${Math.min(fleet.depth, 2)}`} key={fleet.id}>
            <span className={`fleet-organization-icon ${fleet.type}`}>
              {fleet.type === 'authority_group' ? <GroupIcon /> : <TruckIcon />}
            </span>
            <div className="fleet-organization-name">
              <strong>{fleet.name}</strong>
              <span>{fleet.description || 'No description provided'}</span>
            </div>
            <span>{fleet.type === 'authority_group' ? 'Authority group' : 'Fleet'}</span>
            <span>{fleet.childCount ? `${fleet.childCount} direct group${fleet.childCount === 1 ? '' : 's'}` : 'No child groups'}</span>
            <span className={`pill ${fleet.status === 'active' ? 'success' : 'muted'}`}>{fleet.status}</span>
            <button aria-label={`View ${fleet.name}`} className="btn-icon borderless" onClick={() => setSelectedFleet(fleet)} type="button"><EyeIcon /></button>
          </article>
        )) : (
          <div className="empty"><h3>No fleets found</h3><p>{status ? 'Adjust the status filter.' : 'Create your first fleet to begin organizing operations.'}</p></div>
        )}
      </section>

      {selectedFleet ? (
        <FleetDetails
          fleet={selectedFleet}
          fleets={fleets}
          onEdit={(fleetToEdit) => {
            setSelectedFleet(null);
            setEditingFleet(fleetToEdit);
          }}
          onCreateChild={(parentId) => {
            setSelectedFleet(null);
            setCreateParentId(parentId);
            setIsCreating(true);
          }}
          onClose={() => setSelectedFleet(null)}
          onChanged={async (notice) => {
            setSelectedFleet(null);
            setMessage(notice);
            await loadFleets();
          }}
        />
      ) : null}
      {editingFleet ? (
        <FleetEditDialog
          fleet={editingFleet}
          fleets={fleets}
          onClose={() => setEditingFleet(null)}
          onChanged={async (notice) => {
            setEditingFleet(null);
            setMessage(notice);
            await loadFleets();
          }}
        />
      ) : null}
      {isCreating ? (
        <CreateFleetDialog
          fleets={fleets}
          initialParentId={createParentId}
          onClose={() => setIsCreating(false)}
          onCreated={(createdFleet) => {
            setIsCreating(false);
            setFleets((current) => insertCreatedFleet(current, createdFleet));
            setMessage('Fleet created successfully.');
          }}
        />
      ) : null}
      </>
      )}
    </div>
  );
}

function CreateFleetDialog({ fleets, initialParentId, onClose, onCreated }: { fleets: FleetRecord[]; initialParentId?: string; onClose: () => void; onCreated: (fleet: FleetRecord) => void }) {
  const [form, setForm] = React.useState<CreateFleetPayload>({ name: '', description: '', group_type: 'fleet', parent_id: initialParentId });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  return (
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <form
        aria-labelledby="create-fleet-title"
        aria-modal="true"
        className="signup-dialog fleet-create-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={async (event) => {
          event.preventDefault();
          setIsSubmitting(true);
          setError('');
          try {
            const payload = { ...form, name: form.name.trim(), description: form.description?.trim() };
            const response = await createFleet(payload);
            onCreated(normalizeCreatedFleet(response, payload));
          } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Unable to create fleet');
          } finally {
            setIsSubmitting(false);
          }
        }}
        role="dialog"
      >
        <div className="signup-dialog-head">
          <div><span className="fleet-organization-icon fleet"><TruckIcon /></span><div><h2 id="create-fleet-title">Create fleet</h2><span>Add a fleet or authority group.</span></div></div>
          <button aria-label="Close create fleet dialog" className="btn-icon borderless" onClick={onClose} type="button">&times;</button>
        </div>
        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
        <label className="form-group"><span>Name</span><input autoFocus required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Lagos Delivery Fleet" /></label>
        <label className="form-group"><span>Description</span><textarea required value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Describe this operational group" /></label>
        <label className="form-group"><span>Type</span><select value={form.group_type} onChange={(event) => setForm((current) => ({ ...current, group_type: event.target.value as CreateFleetPayload['group_type'] }))}><option value="fleet">Fleet</option><option value="authority">Authority group</option></select></label>
        <label className="form-group"><span>Parent group <small>(optional)</small></span><select value={form.parent_id ?? ''} onChange={(event) => setForm((current) => ({ ...current, parent_id: event.target.value || undefined }))}><option value="">No parent group</option>{fleets.filter((fleet) => fleet.type === 'authority_group').map((fleet) => <option key={fleet.id} value={fleet.id}>{fleet.name}</option>)}</select></label>
        <div className="kyc-dialog-actions"><button className="btn" onClick={onClose} type="button">Cancel</button><button className="btn btn-primary" disabled={isSubmitting} type="submit">{isSubmitting ? 'Creating...' : 'Create fleet'}</button></div>
      </form>
    </div>
  );
}

function FleetDetails({ fleet, fleets, onClose, onChanged, onCreateChild, onEdit }: { fleet: FleetRecord; fleets: FleetRecord[]; onClose: () => void; onChanged: (message: string) => Promise<void>; onCreateChild: (parentId: string) => void; onEdit: (fleet: FleetRecord) => void }) {
  const [form, setForm] = React.useState<UpdateFleetPayload>({ name: fleet.name, description: fleet.description, parent_id: fleet.parentId || null });
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    getFleetGroup(fleet.id)
      .then((response) => {
        const source = unwrapCreatedFleet(response);
        setForm({
          name: readString(source, 'name') ?? fleet.name,
          description: readString(source, 'description') ?? fleet.description,
          parent_id: readString(source, 'parent_id', 'parentId') ?? null,
        });
      })
      .catch((requestError) => setError(getRequestError(requestError, 'Unable to load fleet group')))
      .finally(() => setIsLoadingDetails(false));
  }, [fleet]);

  const parentName = fleets.find((candidate) => candidate.id === form.parent_id)?.name ?? (fleet.type === 'authority_group' ? 'None' : 'Top level');

  async function runAction(action: () => Promise<unknown>, notice: string) {
    setIsSubmitting(true);
    setError('');
    try {
      await action();
      await onChanged(notice);
    } catch (requestError) {
      setError(getRequestError(requestError, 'Unable to update fleet group'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <section
        aria-labelledby="fleet-details-title"
        aria-modal="true"
        className="signup-dialog fleet-group-details-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
      >
        <div className="signup-dialog-head">
          <h2 id="fleet-details-title">{fleet.name}</h2>
          <button aria-label="Close fleet details" className="btn-icon borderless" onClick={onClose} type="button">&times;</button>
        </div>
        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
        <dl className="fleet-group-summary">
          <div><dt>Type</dt><dd>{fleet.type === 'authority_group' ? 'Authority group' : 'Fleet'}</dd></div>
          <div><dt>Status</dt><dd>{capitalize(fleet.status)}</dd></div>
          <div><dt>Parent group</dt><dd>{parentName}</dd></div>
          <div><dt>Direct groups</dt><dd>{fleet.childCount}</dd></div>
        </dl>
        <div className="fleet-group-description"><strong>Description</strong><p>{form.description || 'No description provided'}</p></div>
        <div className="fleet-group-resource-links">
          {fleet.type === 'authority_group' ? (
            <button className="btn" onClick={() => onCreateChild(fleet.id)} type="button"><span aria-hidden="true">+</span> Create child group</button>
          ) : (
            <>
              <button className="btn" type="button"><TruckIcon /> Vehicles</button>
              <button className="btn" type="button"><UserIcon /> Drivers</button>
              <button className="btn" type="button"><CalendarIcon /> Assignments</button>
            </>
          )}
        </div>
        <div className="kyc-dialog-actions fleet-group-dialog-actions">
          {confirmDelete ? <button className="btn btn-danger" disabled={isSubmitting} onClick={() => void runAction(() => deleteFleetGroup(fleet.id), 'Fleet group deleted successfully.')} type="button">Confirm delete</button> : <button className="btn btn-danger" disabled={isSubmitting} onClick={() => setConfirmDelete(true)} type="button">Delete</button>}
          <button className="btn" disabled={isSubmitting} onClick={() => void runAction(() => fleet.status === 'active' ? deactivateFleetGroup(fleet.id) : activateFleetGroup(fleet.id), `Fleet group ${fleet.status === 'active' ? 'deactivated' : 'activated'} successfully.`)} type="button">{fleet.status === 'active' ? 'Deactivate' : 'Activate'}</button>
          <button className="btn btn-primary" disabled={isSubmitting || isLoadingDetails} onClick={() => onEdit({ ...fleet, name: form.name ?? fleet.name, description: form.description ?? fleet.description, parentId: form.parent_id ?? '' })} type="button">{isLoadingDetails ? 'Loading...' : 'Edit'}</button>
        </div>
      </section>
    </div>
  );
}

function FleetEditDialog({ fleet, fleets, onClose, onChanged }: { fleet: FleetRecord; fleets: FleetRecord[]; onClose: () => void; onChanged: (message: string) => Promise<void> }) {
  const [form, setForm] = React.useState<UpdateFleetPayload>({ name: fleet.name, description: fleet.description, parent_id: fleet.parentId || null });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const descendants = new Set(getDescendantIds(fleets, fleet.id));
  const parentOptions = fleets.filter((candidate) =>
    candidate.type === 'authority_group'
    && candidate.id !== fleet.id
    && !descendants.has(candidate.id));

  return (
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <form
        aria-labelledby="fleet-edit-title"
        aria-modal="true"
        className="signup-dialog fleet-create-dialog"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={async (event) => {
          event.preventDefault();
          setIsSubmitting(true);
          setError('');
          try {
            await updateFleetGroup(fleet.id, {
              ...form,
              name: form.name?.trim(),
              description: form.description?.trim(),
            });
            await onChanged('Fleet group updated successfully.');
          } catch (requestError) {
            setError(getRequestError(requestError, 'Unable to update fleet group'));
          } finally {
            setIsSubmitting(false);
          }
        }}
        role="dialog"
      >
        <div className="signup-dialog-head">
          <div><span className={`fleet-organization-icon ${fleet.type}`}>{fleet.type === 'authority_group' ? <GroupIcon /> : <TruckIcon />}</span><div><h2 id="fleet-edit-title">Edit {fleet.name}</h2><span>Update group details or move it.</span></div></div>
          <button aria-label="Close edit fleet group" className="btn-icon borderless" onClick={onClose} type="button">&times;</button>
        </div>
        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
        <label className="form-group"><span>Name</span><input autoFocus required maxLength={120} value={form.name ?? ''} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label>
        <label className="form-group"><span>Description</span><textarea value={form.description ?? ''} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} /></label>
        <label className="form-group"><span>Parent group</span><select value={form.parent_id ?? ''} onChange={(event) => setForm((current) => ({ ...current, parent_id: event.target.value || null }))}><option value="">Top level</option>{parentOptions.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.name}</option>)}</select></label>
        <div className="kyc-dialog-actions"><button className="btn" onClick={onClose} type="button">Cancel</button><button className="btn btn-primary" disabled={isSubmitting} type="submit">{isSubmitting ? 'Saving...' : 'Save changes'}</button></div>
      </form>
    </div>
  );
}

function normalizeFleets(response: unknown): FleetRecord[] {
  const records = getFleetSource(response);
  const parentIds = new Set(records.map((record) => readString(record, 'parent_id', 'parentId')).filter(Boolean));

  return records.map((fleet, index) => {
    const id = readString(fleet, 'id', 'fleet_id', 'group_id') ?? String(index);
    const type = normalizeType(readString(fleet, 'type', 'group_type', 'kind'), parentIds.has(id));
    const children = readNumber(fleet, 'child_count', 'children_count', 'direct_groups_count')
      ?? (Array.isArray(fleet.children) ? fleet.children.length : 0);
    return {
      id,
      name: readString(fleet, 'name', 'fleet_name', 'group_name') ?? 'Unnamed fleet',
      description: readString(fleet, 'description', 'summary') ?? '',
      type,
      status: (readString(fleet, 'status') ?? (fleet.is_active === false ? 'inactive' : 'active')).toLowerCase(),
      parentId: readString(fleet, 'parent_id', 'parentId') ?? '',
      childCount: children,
      depth: readNumber(fleet, 'depth', 'level') ?? (readString(fleet, 'parent_id', 'parentId') ? 1 : 0),
    };
  });
}

function normalizeCreatedFleet(response: Record<string, unknown>, payload: CreateFleetPayload): FleetRecord {
  const source = unwrapCreatedFleet(response);
  return {
    id: readString(source, 'id', 'fleet_id', 'group_id') ?? `pending-${Date.now()}`,
    name: readString(source, 'name', 'fleet_name', 'group_name') ?? payload.name,
    description: readString(source, 'description', 'summary') ?? payload.description ?? '',
    type: normalizeType(readString(source, 'type', 'group_type', 'kind') ?? payload.group_type, false),
    status: (readString(source, 'status') ?? 'active').toLowerCase(),
    parentId: readString(source, 'parent_id', 'parentId') ?? payload.parent_id ?? '',
    childCount: readNumber(source, 'child_count', 'children_count', 'direct_groups_count') ?? 0,
    depth: 0,
  };
}

function unwrapCreatedFleet(response: Record<string, unknown>): Record<string, unknown> {
  for (const key of ['fleet', 'group', 'data', 'result']) {
    if (isObject(response[key])) return unwrapCreatedFleet(response[key] as Record<string, unknown>);
  }
  return response;
}

function insertCreatedFleet(fleets: FleetRecord[], createdFleet: FleetRecord) {
  if (!createdFleet.parentId) return [...fleets, createdFleet];

  const parentIndex = fleets.findIndex((fleet) => fleet.id === createdFleet.parentId);
  if (parentIndex === -1) return [...fleets, createdFleet];

  const parent = fleets[parentIndex];
  const next = [...fleets];
  next[parentIndex] = {
    ...parent,
    childCount: parent.childCount + 1,
  };
  const child = { ...createdFleet, depth: parent.depth + 1 };
  let insertAt = parentIndex + 1;

  while (insertAt < next.length && next[insertAt].depth > parent.depth) insertAt += 1;
  next.splice(insertAt, 0, child);
  return next;
}

function getDescendantIds(fleets: FleetRecord[], parentId: string) {
  const descendants: string[] = [];
  const pending = [parentId];

  while (pending.length) {
    const current = pending.shift();
    const children = fleets.filter((fleet) => fleet.parentId === current).map((fleet) => fleet.id);
    descendants.push(...children);
    pending.push(...children);
  }

  return descendants;
}

function getRequestError(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function capitalize(value: string) {
  return value ? `${value[0].toUpperCase()}${value.slice(1)}` : value;
}

function getFleetSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return expandFleetRecords(response.filter(isObject));
  if (!isObject(response)) return [];
  for (const key of ['fleets', 'sub_fleets', 'groups', 'items', 'results', 'authority', 'data']) {
    const value = response[key];
    if (Array.isArray(value)) return expandFleetRecords(value.filter(isObject));
    if (isObject(value)) {
      const nested = getFleetSource(value);
      if (nested.length) return nested;
    }
  }
  return [];
}

function expandFleetRecords(records: Record<string, unknown>[], depth = 0, parentId = ''): Record<string, unknown>[] {
  return records.flatMap((record) => {
    const id = readString(record, 'id', 'fleet_id', 'group_id') ?? '';
    const normalized = {
      ...record,
      ...(record.depth === undefined && record.level === undefined ? { depth } : {}),
      ...(parentId && record.parent_id === undefined && record.parentId === undefined ? { parent_id: parentId } : {}),
    };
    const children = Array.isArray(record.children) ? record.children.filter(isObject) : [];
    return [normalized, ...expandFleetRecords(children, depth + 1, id)];
  });
}

function normalizeType(value: string | undefined, hasChildren: boolean): FleetRecord['type'] {
  return value === 'authority_group' || value === 'authority' || value === 'group' || hasChildren ? 'authority_group' : 'fleet';
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) if (typeof source[key] === 'string' && source[key]) return source[key] as string;
  return undefined;
}

function readNumber(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) if (typeof source[key] === 'number') return source[key] as number;
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function EyeIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" /></svg>; }
function TruckIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h12v10H3zM15 10h4l2 3v3h-6z" /><circle cx="7" cy="18" r="2" /><circle cx="18" cy="18" r="2" /></svg>; }
function GroupIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="6" cy="6" r="3" /><circle cx="18" cy="12" r="3" /><circle cx="7" cy="19" r="3" /><path d="M9 7.5l6 3M15.5 14l-6 3.5" /></svg>; }
function UserIcon() { return <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="7" r="4" /><path d="M5 21v-2a7 7 0 0114 0v2" /></svg>; }
function CalendarIcon() { return <svg width="21" height="21" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>; }
